import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IMoney } from '../../types/IMoney';
import {
    IInvoiceItemOrderComparisonDto,
    IInvoiceItemQuantityComparisonDto,
    IInvoiceOrderComparisonDto,
    IInvoiceValueComparisonDto,
    InvoicedQuantityStatus,
} from './IInvoiceOrderComparison';

/**
 * The six compared fields, as the translation key of their German source text. Used for the
 * "matching fields" line of the comparison block, which names what was held against the order and
 * found equal — the half of the statement a deviation table cannot show.
 */
export const COMPARED_FIELD_PRICE = marker('Preis');
export const COMPARED_FIELD_PRICE_QUANTITY = marker('Preismenge');
export const COMPARED_FIELD_CURRENCY = marker('Währung');
export const COMPARED_FIELD_TAX = marker('Steuersatz');
export const COMPARED_FIELD_ARTICLE_NUMBER = marker('Artikelnummer');
export const COMPARED_FIELD_UNIT = marker('Einheit');

function roundToCents(value: number): number {
    return Math.round(value * 100) / 100;
}

/**
 * One compared field of an invoice item. Internal model, so a rename on the wire is absorbed here
 * and never reaches a component input.
 */
export class InvoiceValueComparison<T> {
    public readonly invoiceValue: T | null;
    public readonly orderValue: T | null;
    public readonly isDifferent: boolean;

    constructor(dto: IInvoiceValueComparisonDto<T>) {
        this.invoiceValue = dto.invoiceValue ?? null;
        this.orderValue = dto.orderValue ?? null;
        this.isDifferent = dto.isDifferent;
    }

    public static from<T>(dto: IInvoiceValueComparisonDto<T> | undefined): InvoiceValueComparison<T> | null {
        return dto === undefined ? null : new InvoiceValueComparison<T>(dto);
    }
}

/** Quantity statement of an invoice item against its order position. */
export class InvoiceItemQuantityComparison {
    public readonly invoiceQuantity: number;
    public readonly orderedQuantity: number;
    public readonly cumulativeInvoicedQuantity: number;
    public readonly status: InvoicedQuantityStatus;
    public readonly isDifferent: boolean;

    constructor(dto: IInvoiceItemQuantityComparisonDto) {
        this.invoiceQuantity = dto.invoiceQuantity;
        this.orderedQuantity = dto.orderedQuantity;
        this.cumulativeInvoicedQuantity = dto.cumulativeInvoicedQuantity;
        this.status = dto.status;
        this.isDifferent = dto.isDifferent;
    }

    public get isOverInvoiced(): boolean {
        return this.status === InvoicedQuantityStatus.OVER_INVOICED;
    }

    public get isFullyInvoiced(): boolean {
        return this.status === InvoicedQuantityStatus.FULLY_INVOICED;
    }

    /**
     * Quantity still open on the order position. Bounded by the ordered quantity on both ends: a
     * credit-note overhang leaves a negative cumulative quantity, and without the upper bound the
     * line would offer more as open than was ever ordered.
     */
    public get openQuantity(): number {
        const ordered = Math.max(this.orderedQuantity, 0);

        return Math.min(Math.max(this.orderedQuantity - this.cumulativeInvoicedQuantity, 0), ordered);
    }

    public get overInvoicedQuantity(): number {
        return Math.max(this.cumulativeInvoicedQuantity - this.orderedQuantity, 0);
    }

    /**
     * Share of the ordered quantity already invoiced, 0–100. A credit-note overhang (negative
     * cumulative quantity) is clamped to 0 instead of drawing a bar backwards.
     */
    public get invoicedPercent(): number {
        if (this.orderedQuantity <= 0) {
            return this.cumulativeInvoicedQuantity > 0 ? 100 : 0;
        }

        return Math.min(Math.max((this.cumulativeInvoicedQuantity / this.orderedQuantity) * 100, 0), 100);
    }

    /** Share of the bar the over-invoiced part takes when more was invoiced than ordered, 0–100. */
    public get overInvoicedPercent(): number {
        if (!this.isOverInvoiced || this.cumulativeInvoicedQuantity <= 0) {
            return 0;
        }

        return (this.overInvoicedQuantity / this.cumulativeInvoicedQuantity) * 100;
    }
}

/**
 * Deviations of one invoice item from its order item. Members the backend omits become `null` here,
 * so the template never has to tell "field not sent" from "field is null".
 */
export class InvoiceItemOrderComparison {
    public readonly invoiceItemId: string;
    public readonly linkedToOrder: boolean;
    public readonly isDifferent: boolean;
    public readonly orderId: string | null;
    public readonly orderItemPosition: number | null;

    public readonly price: InvoiceValueComparison<IMoney> | null;
    public readonly priceQuantity: InvoiceValueComparison<number> | null;
    public readonly currency: InvoiceValueComparison<string> | null;
    public readonly taxInPercent: InvoiceValueComparison<number> | null;
    public readonly supplierArticleNumber: InvoiceValueComparison<string> | null;
    public readonly unit: InvoiceValueComparison<string> | null;
    public readonly quantity: InvoiceItemQuantityComparison | null;

    constructor(dto: IInvoiceItemOrderComparisonDto) {
        this.invoiceItemId = dto.invoiceItemId;
        this.linkedToOrder = dto.linkedToOrder;
        this.isDifferent = dto.isDifferent;
        this.orderId = dto.orderId ?? null;
        this.orderItemPosition = dto.orderItemPosition ?? null;
        this.price = InvoiceValueComparison.from(dto.price);
        this.priceQuantity = InvoiceValueComparison.from(dto.priceQuantity);
        this.currency = InvoiceValueComparison.from(dto.currency);
        this.taxInPercent = InvoiceValueComparison.from(dto.taxInPercent);
        this.supplierArticleNumber = InvoiceValueComparison.from(dto.supplierArticleNumber);
        this.unit = InvoiceValueComparison.from(dto.unit);
        this.quantity = dto.quantity === undefined ? null : new InvoiceItemQuantityComparison(dto.quantity);
    }

    /**
     * Price and price quantity share one row: a price is only an assertion together with the quantity
     * it is quoted for, so reporting them apart marks every position quoted per 100 as a price
     * deviation although its price per unit matches.
     */
    public get priceDeviates(): boolean {
        return (this.price?.isDifferent ?? false) || (this.priceQuantity?.isDifferent ?? false);
    }

    public get currencyDeviates(): boolean {
        return this.currency?.isDifferent ?? false;
    }

    public get taxDeviates(): boolean {
        return this.taxInPercent?.isDifferent ?? false;
    }

    public get articleNumberDeviates(): boolean {
        return this.supplierArticleNumber?.isDifferent ?? false;
    }

    public get unitDeviates(): boolean {
        return this.unit?.isDifferent ?? false;
    }

    /**
     * Only over-invoicing is a deviation; a partial and a fully invoiced position are neutral. The
     * verdict is the backend's, so the counters of the position and of the order group can never
     * disagree about the same quantity.
     */
    public get quantityDeviates(): boolean {
        return this.quantity?.isDifferent ?? false;
    }

    /** Number of deviating rows — the figure the counters in the header and at the tab show. */
    public get deviationCount(): number {
        return [
            this.priceDeviates,
            this.currencyDeviates,
            this.taxDeviates,
            this.articleNumberDeviates,
            this.unitDeviates,
            this.quantityDeviates,
        ].filter(Boolean).length;
    }

    /**
     * Fields that were compared and found equal, in the order of the table. Price and price quantity
     * are listed together or not at all — they share one row, so naming one of them as matching next
     * to that very row would contradict it. Prices in different currencies were not compared and are
     * therefore not named as matching either.
     */
    public get matchingFields(): string[] {
        const matching: string[] = [];
        if (this.price && this.priceQuantity && !this.priceDeviates && !this.currencyDeviates) {
            matching.push(COMPARED_FIELD_PRICE, COMPARED_FIELD_PRICE_QUANTITY);
        }
        if (this.currency && !this.currency.isDifferent) {
            matching.push(COMPARED_FIELD_CURRENCY);
        }
        if (this.taxInPercent && !this.taxInPercent.isDifferent) {
            matching.push(COMPARED_FIELD_TAX);
        }
        if (this.supplierArticleNumber && !this.supplierArticleNumber.isDifferent) {
            matching.push(COMPARED_FIELD_ARTICLE_NUMBER);
        }
        if (this.unit && !this.unit.isDifferent) {
            matching.push(COMPARED_FIELD_UNIT);
        }

        return matching;
    }

    public get invoicePriceQuantity(): number {
        return this.priceQuantity?.invoiceValue ?? 1;
    }

    public get orderPriceQuantity(): number {
        return this.priceQuantity?.orderValue ?? 1;
    }

    /** True while both sides quote their price for a single unit — then no conversion line is needed. */
    public get bothPriceQuantitiesAreOne(): boolean {
        return this.invoicePriceQuantity === 1 && this.orderPriceQuantity === 1;
    }

    public get invoiceUnitPrice(): number | null {
        return InvoiceItemOrderComparison.unitPrice(this.price?.invoiceValue ?? null, this.invoicePriceQuantity);
    }

    public get orderUnitPrice(): number | null {
        return InvoiceItemOrderComparison.unitPrice(this.price?.orderValue ?? null, this.orderPriceQuantity);
    }

    /**
     * Difference per single unit — the only figure that is comparable across differing price
     * quantities. Unrounded: at a price quantity of 1000 the difference per unit is a fraction of a
     * cent even when the quoted prices are euros apart.
     *
     * <p>Not defined across currencies: subtracting two amounts of different currencies would produce
     * a number that looks like a price difference and is none.
     */
    public get unitPriceDifference(): number | null {
        const invoicePrice = this.invoiceUnitPrice;
        const orderPrice = this.orderUnitPrice;

        if (invoicePrice === null || orderPrice === null || this.currencyDeviates) {
            return null;
        }

        return invoicePrice - orderPrice;
    }

    public get unitPriceDifferencePercent(): number | null {
        const difference = this.unitPriceDifference;
        const orderPrice = this.orderUnitPrice;

        return difference === null || !orderPrice ? null : (difference / orderPrice) * 100;
    }

    /**
     * Both sides charge the same price per unit and only the price quantity differs. Still a deviation
     * of the recorded fields, but not a price difference, so the table says so instead of printing a
     * difference of zero.
     *
     * <p>Judged on the difference scaled back to the price quantity the invoice itself quotes, and
     * rounded there: a price quoted per 60 minutes and the same price quoted per minute differ by a
     * fraction of a cent per unit and are the same price, while two prices quoted per 1000 that are
     * four euros apart are not — although their difference per unit is smaller still.
     */
    public get unitPriceIsEqual(): boolean {
        const difference = this.unitPriceDifference;

        return difference !== null && roundToCents(difference * this.invoicePriceQuantity) === 0;
    }

    private static unitPrice(money: IMoney | null, priceQuantity: number): number | null {
        return money === null || !priceQuantity ? null : money.amount / priceQuantity;
    }
}

/**
 * Anti-corruption layer over the order-comparison endpoint: the data service hands this class out,
 * never the wire DTO.
 */
export class InvoiceOrderComparison {
    public readonly invoiceId: string;

    /** A credit note takes its quantities back and expects no goods receipt. */
    public readonly creditNote: boolean;
    public readonly linkedToOrder: boolean;
    public readonly isDifferent: boolean;
    public readonly itemsById: Record<string, InvoiceItemOrderComparison>;

    /**
     * Deviating positions per order — the counter on the order group header. Counted over the same
     * rows the block of each position shows, so group and position can never disagree. The total it
     * is held against is the number of positions in the group on screen, not a second count from
     * here: an item whose order cannot be read carries no order id and would shrink that total.
     */
    public readonly deviationsByOrderId: Record<string, number>;

    constructor(dto: IInvoiceOrderComparisonDto) {
        this.invoiceId = dto.invoiceId;
        this.creditNote = dto.creditNote;
        this.linkedToOrder = dto.linkedToOrder;
        this.isDifferent = dto.isDifferent;
        this.itemsById = InvoiceOrderComparison.mapItems(dto.items);
        this.deviationsByOrderId = InvoiceOrderComparison.countDeviationsPerOrder(this.itemsById);
    }

    private static mapItems(items: IInvoiceItemOrderComparisonDto[]): Record<string, InvoiceItemOrderComparison> {
        const mapped: Record<string, InvoiceItemOrderComparison> = {};
        items.forEach((item) => {
            mapped[item.invoiceItemId] = new InvoiceItemOrderComparison(item);
        });

        return mapped;
    }

    private static countDeviationsPerOrder(items: Record<string, InvoiceItemOrderComparison>): Record<string, number> {
        const counts: Record<string, number> = {};
        Object.values(items).forEach((item) => {
            if (item.deviationCount > 0 && item.orderId) {
                counts[item.orderId] = (counts[item.orderId] ?? 0) + 1;
            }
        });

        return counts;
    }
}
